import { ACK_STATUS } from '../catalog/ack';
import { EVENT_DIRECTION, FEED_FILTER } from '../catalog/link';
import type { FeedFilter, SendTransport } from '../catalog/link';
import { decodeInbound } from './decode';
import { describeDocument, describeReading } from './describe';
import { redactDocument } from './redact';
import type { RcuReading } from './readings';
import type { RcuDocument } from '../wire';

/**
 * The event feed: one record per thing that crossed the link.
 *
 * An outgoing record keeps the document and the bytes that were published, so
 * the drawer can show exactly what was sent; an incoming one keeps the text and
 * what the decoder made of it. Both keep whatever the link itself reported —
 * a refused publish, an unreadable reply — so a failure is visible where it
 * happened rather than in a console the technician cannot open.
 */

export interface OutgoingEvent {
  readonly id: string;
  readonly at: string;
  readonly direction: typeof EVENT_DIRECTION.OUT;
  readonly topic: string;
  readonly summary: string;
  readonly document: RcuDocument;
  readonly raw: string;
  readonly ok: boolean;
  readonly transport: SendTransport;
  readonly detail: string;
}

export interface IncomingEvent {
  readonly id: string;
  readonly at: string;
  readonly direction: typeof EVENT_DIRECTION.IN;
  readonly topic: string;
  readonly summary: string;
  readonly raw: string;
  readonly reading: RcuReading;
}

export interface LinkEvent {
  readonly id: string;
  readonly at: string;
  readonly direction: typeof EVENT_DIRECTION.LINK;
  readonly summary: string;
  readonly detail: string;
}

export type RcuEvent = OutgoingEvent | IncomingEvent | LinkEvent;

export interface EventIdentity {
  readonly id: string;
  readonly at: string;
}

/**
 * The document is redacted on the way in: a `wificonfig` password must never
 * reach a screen, and the feed is history rather than a resend buffer.
 */
export const outgoingEvent = (
  identity: EventIdentity,
  fields: {
    readonly topic: string;
    readonly document: RcuDocument;
    readonly ok: boolean;
    readonly transport: SendTransport;
    readonly detail: string;
  },
): OutgoingEvent => {
  const document = redactDocument(fields.document);
  return {
    ...identity,
    direction: EVENT_DIRECTION.OUT,
    topic: fields.topic,
    summary: describeDocument(document),
    document,
    raw: JSON.stringify(document),
    ok: fields.ok,
    transport: fields.transport,
    detail: fields.detail,
  };
};

export const incomingEvent = (
  identity: EventIdentity,
  message: { readonly topic: string; readonly raw: string },
): IncomingEvent => {
  const reading = decodeInbound(message.raw);
  return {
    ...identity,
    direction: EVENT_DIRECTION.IN,
    topic: message.topic,
    summary: describeReading(reading),
    raw: message.raw,
    reading,
  };
};

export const linkEvent = (
  identity: EventIdentity,
  message: { readonly summary: string; readonly detail: string },
): LinkEvent => ({
  ...identity,
  direction: EVENT_DIRECTION.LINK,
  summary: message.summary,
  detail: message.detail,
});

/**
 * A reading the device answered with a refusal rather than a result: a `fail` on
 * a mapping, coredump, OTA or log command is a problem the drawer can filter to,
 * exactly like a reply the console could not read at all. Every reading that
 * carries an error line carries an ack status beside it, so the status is the
 * whole test.
 */
const refused = (reading: RcuReading): boolean => {
  if (reading.kind === 'unknown') return true;
  return 'status' in reading && reading.status === ACK_STATUS.FAIL;
};

/** Whether an event is worth the drawer's attention when the filter is `errors`. */
export const isFailure = (event: RcuEvent): boolean => {
  switch (event.direction) {
    case EVENT_DIRECTION.OUT:
      return !event.ok;
    case EVENT_DIRECTION.IN:
      return refused(event.reading);
    case EVENT_DIRECTION.LINK:
      return false;
    default:
      return false;
  }
};

/** Which events a filter chip shows. */
export const matchesFilter = (event: RcuEvent, filter: FeedFilter): boolean => {
  switch (filter) {
    case FEED_FILTER.ALL:
      return true;
    case FEED_FILTER.ERRORS:
      return isFailure(event);
    default:
      return event.direction === filter;
  }
};
